import { forwardRef } from "react";
import { BadgeCheck } from "lucide-react";
import { PassTemplateView } from "@/components/events/PassTemplateView";

const serialText = (p) => `SERIAL NO. ${p.serial_prefix || ""}${String(p.serial_no).padStart(3, "0")}`;

export const PassCard = forwardRef(({ pass, qr, widthPx = 400 }, ref) => {
  const tmpl = pass.pass_template || {};
  const useTmpl = !!(tmpl.enabled && tmpl.image && (tmpl.elements || []).length && qr);
  const hasSerialEl = (tmpl.elements || []).some((e) => e.bind === "__serial");
  const candPhoto = (pass.fields || []).filter((f) => f.type === "photo").map((f) => pass.data?.[f.key]).find((v) => typeof v === "string" && v.startsWith("data:image"));
  const ctx = {
    data: pass.data || {}, serial_no: pass.serial_no, candidate_name: pass.candidate_name,
    event_name: pass.event_name, qrDataUrl: qr, serial_prefix: pass.serial_prefix || "", tag: pass.tag || "",
  };

  return (
    <div ref={ref} className="bg-white" data-testid="pass-card">
      {pass.banner && (
        <img src={pass.banner} alt="Event banner" className="block mx-auto mb-2 rounded-lg object-cover" style={{ width: useTmpl ? widthPx : "100%", maxHeight: 220 }} data-testid="pass-banner" />
      )}
      {useTmpl ? (
        <div className="relative w-fit mx-auto">
          <PassTemplateView template={tmpl} ctx={ctx} widthPx={widthPx} />
          {!hasSerialEl && pass.serial_no > 0 && (
            <div className="absolute bottom-2 left-2 bg-slate-900/85 text-white text-[11px] font-bold px-2.5 py-1 rounded-lg" data-testid="passdl-serial-badge">{serialText(pass)}</div>
          )}
        </div>
      ) : (
        <div className="border border-border rounded-xl p-5 text-center space-y-3">
          {candPhoto && <img src={candPhoto} alt="Candidate" className="h-20 w-20 rounded-xl object-cover border mx-auto" data-testid="passdl-photo" />}
          {pass.serial_no > 0 && <p className="inline-block text-[11px] font-bold tracking-wider text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-3 py-1" data-testid="pass-serial-no">{serialText(pass)}</p>}
          {pass.tag && <p className="inline-block text-[11px] font-bold px-2.5 py-1 rounded-full text-amber-800 bg-amber-100 border border-amber-300 ml-1" data-testid="passdl-tag">{pass.tag}</p>}
          {pass.candidate_name && <p className="font-heading text-xl font-bold" data-testid="pass-candidate-name">{pass.candidate_name}</p>}
          <p className="text-sm font-semibold text-slate-600" data-testid="pass-event-name">{pass.event_name}</p>
          {pass.status === "approved" && <div className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold px-3 py-1 rounded-full text-sm" data-testid="pass-approved-badge"><BadgeCheck className="h-4 w-4" /> APPROVED</div>}
          <div className="text-left border-t border-dashed pt-3 space-y-1.5" data-testid="pass-details">
            {(pass.fields || []).filter((f) => f.type !== "photo").map((f) => (
              <div key={f.key} className="flex justify-between gap-3 text-sm"><span className="text-slate-500">{f.label}</span><span className="font-semibold text-right">{pass.data?.[f.key] || "—"}</span></div>
            ))}
          </div>
          {qr && <img src={qr} alt="QR" className="h-28 w-28 mx-auto" data-testid="pass-qr" />}
        </div>
      )}
    </div>
  );
});
PassCard.displayName = "PassCard";
